import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Loader, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import Navbar from '../../components/Navbar';
import usePageTitle from '../../hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewSubmissionPage = ({ user, onLogout }) => {
  const { invitationCode } = useParams();
  const navigate = useNavigate();
  usePageTitle('Bewertung abgeben');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);

  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventDate, setEventDate] = useState('');

  useEffect(() => {
    validateInvitation();
  }, [invitationCode]);

  const validateInvitation = async () => {
    try {
      const response = await axios.get(`${API}/review-invitation/${invitationCode}`);
      setInvitation(response.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError('invalid');
      } else if (status === 410) {
        setError('expired');
      } else if (status === 409) {
        setError('used');
      } else {
        setError('unknown');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Bitte wählen Sie eine Sternebewertung');
      return;
    }

    if (!title.trim()) {
      toast.error('Bitte geben Sie einen Titel ein');
      return;
    }

    if (title.trim().length < 3) {
      toast.error('Der Titel muss mindestens 3 Zeichen enthalten');
      return;
    }

    if (!comment.trim()) {
      toast.error('Bitte geben Sie einen Kommentar ein');
      return;
    }

    if (comment.trim().length < 10) {
      toast.error('Der Kommentar muss mindestens 10 Zeichen enthalten');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/review-invitation/${invitationCode}/submit`, {
        rating,
        title,
        comment,
        customer_name: invitation?.customer_name || 'Kunde',
        event_type: eventType || null,
        event_date: eventDate || null
      });

      setSubmitted(true);
      toast.success('Vielen Dank für Ihre Bewertung!');

      // Redirect to home page after delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      let message = 'Fehler beim Absenden der Bewertung';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error - extract first message
        message = detail[0]?.msg || message;
      }
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`w-10 h-10 ${
                star <= (hoverRating || rating)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="pt-24 flex items-center justify-center">
          <Loader className="w-8 h-8 animate-spin text-yellow-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              {error === 'invalid' && (
                <>
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Ungültiger Link</h1>
                  <p className="text-gray-600">
                    Dieser Einladungslink ist ungültig. Bitte überprüfen Sie den Link oder
                    kontaktieren Sie den Dienstleister für einen neuen Link.
                  </p>
                </>
              )}
              {error === 'expired' && (
                <>
                  <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Link abgelaufen</h1>
                  <p className="text-gray-600">
                    Dieser Einladungslink ist abgelaufen. Einladungen sind 14 Tage gültig.
                    Bitte kontaktieren Sie den Dienstleister für einen neuen Link.
                  </p>
                </>
              )}
              {error === 'used' && (
                <>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Bereits bewertet</h1>
                  <p className="text-gray-600">
                    Mit diesem Link wurde bereits eine Bewertung abgegeben.
                    Vielen Dank für Ihr Feedback!
                  </p>
                </>
              )}
              {error === 'unknown' && (
                <>
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Fehler</h1>
                  <p className="text-gray-600">
                    Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.
                  </p>
                </>
              )}
              <button
                onClick={() => navigate('/')}
                className="mt-6 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-xl transition-colors"
              >
                Zur Startseite
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Vielen Dank!</h1>
              <p className="text-gray-600 mb-4">
                Ihre Bewertung wurde erfolgreich übermittelt.
                Sie werden gleich zur Startseite weitergeleitet.
              </p>
              <Loader className="w-6 h-6 animate-spin text-yellow-600 mx-auto" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Bewertung für {invitation?.provider?.company_name}
            </h1>
            <p className="text-gray-600">
              Teilen Sie Ihre Erfahrung mit anderen Kunden
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Ihre Bewertung *
                </label>
                <div className="flex justify-center">
                  {renderStars()}
                </div>
                {rating > 0 && (
                  <p className="text-center text-sm text-gray-500 mt-2">
                    {rating === 1 && 'Unzufrieden'}
                    {rating === 2 && 'Weniger zufrieden'}
                    {rating === 3 && 'Zufrieden'}
                    {rating === 4 && 'Sehr zufrieden'}
                    {rating === 5 && 'Ausgezeichnet'}
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Titel Ihrer Bewertung *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="z.B. Tolle Zusammenarbeit!"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none"
                  maxLength={100}
                  required
                />
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ihr Kommentar *
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Beschreiben Sie Ihre Erfahrung mit dem Dienstleister..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none"
                  rows={5}
                  maxLength={1000}
                  required
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {comment.length}/1000 Zeichen (mind. 10)
                </p>
              </div>

              {/* Event Details (Optional) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Art der Veranstaltung (optional)
                  </label>
                  <input
                    type="text"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    placeholder="z.B. Hochzeit, Geburtstag"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Datum der Veranstaltung (optional)
                  </label>
                  <input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Star className="w-5 h-5" />
                    Bewertung absenden
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Mit dem Absenden bestätigen Sie, dass Ihre Bewertung auf dem Profil
                von {invitation?.provider?.company_name} öffentlich angezeigt werden darf.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmissionPage;
